/**
 * Schemas Zod de `/usuarios` (`03-endpoints-draft.md §2`) — TELA-09, 10, 11.
 *
 * **Não existe campo de senha em nenhum corpo deste arquivo.** É a alteração
 * deliberada em relação ao legado (RN-06): o que se envia é convite com link de
 * definição de senha. Senha em texto claro por e-mail é o defeito que a spec manda
 * não copiar, e "definir senha de terceiro" é o caminho mais curto para um gestor
 * assumir a identidade de um corretor (nota ⁶ de `04-roles §3.5`).
 *
 * **Não existe `DELETE /usuarios/{uuid}`.** Desligamento é `POST /{uuid}/desativar`
 * com destino da carteira (RN-07A) — apagar a linha levaria a timeline dos
 * atendimentos dela junto, e a timeline é o registro de auditoria do atendimento
 * (RN-25).
 *
 * Cada mudança de permissão tem rota própria (papel, equipes, unidades,
 * receber-leads, ativar/desativar) em vez de um `PATCH` genérico: as cinco revogam
 * sessões (RN-73) e entram na auditoria com valor anterior e novo (RN-74), e um
 * `PATCH { papel }` que aceitasse tudo junto teria um caminho para cada uma delas
 * escapar.
 */
import { z } from 'zod'

import { zBooleanoFiltro, zBusca, zIdCatalogo, zUuid } from '../comum/campos'
import { schemaPaginacaoOffset } from '../comum/paginacao'
import { PAPEIS } from '../autorizacao/papeis'

/** Mesma normalização de `/auth`: `citext` no banco resolve caixa, aqui apara ruído. */
export const zEmailUsuario = z
  .string({ required_error: 'Informe o e-mail.' })
  .trim()
  .toLowerCase()
  .min(1, 'Informe o e-mail.')
  .max(320, 'E-mail muito longo.')
  .email('Informe um e-mail válido.')

export const zNomeUsuario = z
  .string({ required_error: 'Informe o nome.' })
  .trim()
  .min(3, 'Informe o nome completo.')
  .max(160, 'Nome muito longo.')

/**
 * Celular em partes, como o resto do sistema (`clientes.contato_principal_*`). O
 * E.164 é derivado no servidor por `normalizarTelefone` — a tela manda o que o
 * usuário digitou.
 */
export const schemaCelular = z
  .object({
    ddi: z.string().trim().max(4).default('55'),
    numero: z.string().trim().min(8, 'Número de celular incompleto.').max(20),
  })
  .strict()

export const zPapelUsuario = z.enum(PAPEIS)

/**
 * `POST /usuarios` — TELA-11.
 *
 * `ativo` **não** entra: usuário nasce ativo. Criar alguém já desativado é cadastrar
 * trabalho para ninguém, e o caminho de "vai começar mês que vem" é criar quando
 * começar — ou criar e desativar, que fica registrado na auditoria.
 */
export const schemaCriarUsuario = z
  .object({
    nome: zNomeUsuario,
    email: zEmailUsuario,
    papel: zPapelUsuario,
    /**
     * Unidade de lotação (`usuarios.unidade_id`). Nulável porque o Administrador não
     * pertence a uma unidade — ele responde por todas.
     */
    unidadeId: zIdCatalogo.nullish(),
    equipeIds: z.array(zIdCatalogo).max(20, 'No máximo 20 equipes por usuário.').default([]),
    /**
     * Escopo de leitura do GESTOR (`usuarios_unidades`). Exigir aqui em vez de em
     * uma segunda chamada evita o gestor criado sem escopo, que enxerga lista vazia
     * e parece bug de permissão.
     */
    unidadeEscopoIds: z.array(zIdCatalogo).max(50).default([]),
    celular: schemaCelular.nullish(),
    /** RN-14. Padrão `false`: entrar na escala é decisão explícita. */
    receberLeads: z.boolean().default(false),
    /** FL-03. Padrão `true`: usuário criado e não convidado não consegue entrar. */
    enviarConvite: z.boolean().default(true),
  })
  .strict()

export type EntradaCriarUsuario = z.infer<typeof schemaCriarUsuario>

/**
 * `PATCH /usuarios/{uuid}` — só dado cadastral (TELA-10).
 *
 * Fora daqui, com rota própria: `papel`, `equipeIds`, `unidadeEscopoIds`,
 * `receberLeads` e `ativo`.
 */
export const schemaAtualizarUsuario = z
  .object({
    nome: zNomeUsuario.optional(),
    email: zEmailUsuario.optional(),
    unidadeId: zIdCatalogo.nullish(),
    celular: schemaCelular.nullish(),
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarUsuario = z.infer<typeof schemaAtualizarUsuario>

/** `PATCH /usuarios/{uuid}/papel` — RN-71, RN-72, RN-73. */
export const schemaAlterarPapel = z.object({ papel: zPapelUsuario }).strict()

export type EntradaAlterarPapel = z.infer<typeof schemaAlterarPapel>

/** `PATCH /usuarios/{uuid}/receber-leads` — RN-14. */
export const schemaAlterarReceberLeads = z
  .object({ receberLeads: z.boolean() })
  .strict()

export type EntradaAlterarReceberLeads = z.infer<typeof schemaAlterarReceberLeads>

/**
 * `PUT /usuarios/{uuid}/equipes` — substituição completa, não delta.
 *
 * `PUT` e não `PATCH` porque a TELA-10 é um seletor múltiplo: ela conhece o conjunto
 * final. Um delta ("adicione X, remova Y") exigiria que a tela e o servidor
 * concordassem sobre o estado anterior, e duas abas abertas discordariam.
 */
export const schemaDefinirEquipes = z
  .object({
    equipeIds: z.array(zIdCatalogo).max(20, 'No máximo 20 equipes por usuário.'),
  })
  .strict()

export type EntradaDefinirEquipes = z.infer<typeof schemaDefinirEquipes>

/** `PUT /usuarios/{uuid}/unidades` — escopo do gestor. Exclusivo do Administrador. */
export const schemaDefinirUnidadesEscopo = z
  .object({ unidadeIds: z.array(zIdCatalogo).max(50) })
  .strict()

export type EntradaDefinirUnidadesEscopo = z.infer<typeof schemaDefinirUnidadesEscopo>

/**
 * Destino dos leads em aberto ao desativar alguém (RN-07A / FL-14).
 *
 * União discriminada, e não três campos opcionais: `{ tipo: 'EQUIPE' }` sem
 * `equipeId` precisa ser erro de validação, não uma transferência para `undefined`
 * descoberta em produção.
 */
export const schemaDestinoLeads = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('RESPONSAVEL'), responsavelUuid: zUuid }).strict(),
  z.object({ tipo: z.literal('EQUIPE'), equipeId: zIdCatalogo }).strict(),
  /** `POOL` = `responsavel_id = null`: volta para a fila da equipe atual (RN-16). */
  z.object({ tipo: z.literal('POOL') }).strict(),
])

export type DestinoLeads = z.infer<typeof schemaDestinoLeads>

/**
 * `POST /usuarios/{uuid}/desativar`.
 *
 * Sem `destino`, e havendo carteira aberta, a API responde `409
 * USUARIO_POSSUI_LEADS_EM_ABERTO` com a contagem — e a tela usa esse `409` como
 * **passo do fluxo**: abre a escolha do destino e reenvia. É o que impede o lead
 * órfão silencioso do RN-07A.
 */
export const schemaDesativarUsuario = z
  .object({
    destino: schemaDestinoLeads.optional(),
    motivo: z
      .string({ required_error: 'Informe o motivo da desativação.' })
      .trim()
      .min(10, 'Descreva o motivo com ao menos 10 caracteres.')
      .max(1000, 'Motivo muito longo.'),
  })
  .strict()

export type EntradaDesativarUsuario = z.infer<typeof schemaDesativarUsuario>

/**
 * `POST /usuarios/{uuid}/transferir-leads` — "Transferir Leads" / "Mudar Leads de
 * Equipe" da TELA-10, e o mesmo motor que a desativação usa.
 */
export const schemaTransferirCarteira = z
  .object({
    responsavelUuid: zUuid.nullish(),
    equipeId: zIdCatalogo.optional(),
    /** `false` inclui os terminais — que a transferência recusa item a item (RN-26A). */
    apenasEmAberto: z.boolean().default(true),
    motivo: z
      .string({ required_error: 'Informe o motivo da transferência.' })
      .trim()
      .min(10, 'Descreva o motivo da transferência com ao menos 10 caracteres.')
      .max(1000, 'Motivo muito longo.'),
  })
  .strict()
  .refine(
    (valor) => valor.responsavelUuid !== undefined || valor.equipeId !== undefined,
    'Informe o novo responsável, a nova equipe, ou os dois.',
  )

export type EntradaTransferirCarteira = z.infer<typeof schemaTransferirCarteira>

export const CAMPOS_ORDENACAO_USUARIO = ['nome', 'criadoEm', 'ultimoAcessoEm'] as const

export type CampoOrdenacaoUsuario = (typeof CAMPOS_ORDENACAO_USUARIO)[number]

export const schemaFiltrosUsuario = schemaPaginacaoOffset.extend({
  busca: zBusca,
  papel: zPapelUsuario.optional(),
  ativo: zBooleanoFiltro.optional(),
  unidadeId: zIdCatalogo.optional(),
  equipeId: zIdCatalogo.optional(),
  receberLeads: zBooleanoFiltro.optional(),
  /** RN-76 — os inconsistentes que a TELA-09 sinaliza. */
  semEquipe: z.enum(['true']).optional(),
  /** FL-03 — convite enviado e nunca aceito. */
  convitePendente: z.enum(['true']).optional(),
})

export type FiltrosUsuario = z.infer<typeof schemaFiltrosUsuario>
